import { useEffect, useRef } from 'react';

/**
 * The living background on the sign-in panel.
 *
 * Light moving through a fibre network — which is not decoration for its own
 * sake: it is the thing this business does, drawn as it actually behaves. Light
 * leaves a POP, reaches a splitter, and fans out to the premises behind it. The
 * pulses fade as they travel, because that is what light does over distance and
 * through a splitter, and it makes the picture read as a network rather than as
 * dots sliding along lines.
 *
 * Canvas rather than SVG or CSS. Around fifty moving pieces of light redrawn
 * sixty times a second is far more DOM than it is worth — the browser would be
 * recalculating style and layout for every one of them. On a canvas it is one
 * element and a few hundred draw calls.
 *
 * ## What it will not do
 *
 * Four things, each of which is the difference between a nice touch and a
 * complaint:
 *
 *  - **It stops when nobody is looking.** A sign-in page is exactly the tab
 *    people leave open all afternoon. The loop is cancelled on
 *    `visibilitychange` and restarted on return, so a forgotten tab costs
 *    nothing.
 *  - **It respects `prefers-reduced-motion`.** Somebody who has asked their
 *    system for less movement gets a single composed still frame — not a
 *    blank panel, which would punish them for the preference.
 *  - **It never covers the text.** `pointer-events: none`, painted behind, and
 *    kept faint enough that the heading over it stays comfortably readable.
 *  - **It only exists on wide screens**, because the panel it lives in is
 *    `hidden lg:flex`. No phone spends battery on this.
 *
 * There is no image, no font and no network request here, so the
 * content-security-policy has nothing to allow and there is nothing to load
 * slowly.
 */

/** Nodes live in 0–1 space, so a resize rescales rather than regenerating. */
type Node = { x: number; y: number; kind: 'pop' | 'splitter' | 'premises' };
type Edge = { from: number; to: number };
type Pulse = { edge: number; t: number; speed: number };

/**
 * A tiny deterministic generator.
 *
 * Fixed seed on purpose: the layout is composed rather than random, so it looks
 * the same on every load and on every machine. A panel that rearranges itself
 * each time somebody signs in reads as noise, and it would be impossible to
 * tell a good arrangement from a bad one.
 */
function seeded(seed: number): () => number {
  let value = seed;
  return () => {
    value = (value * 1_664_525 + 1_013_904_223) % 4_294_967_296;
    return value / 4_294_967_296;
  };
}

/**
 * A POP on the left, splitters through the middle, premises fanning right.
 *
 * Built once and shaped like the real topology: three levels, each feeding the
 * next. The tree is what makes the light look like it is going somewhere.
 *
 * **The whole graph is kept in the upper two-thirds.** The panel's bottom-left
 * is where the heading and the strapline sit, and the first arrangement put the
 * POP — the one node with a halo — directly underneath them. Text over a moving
 * glow is text that is harder to read for no gain, so the network stays out of
 * that corner and a mask in `draw()` takes care of whatever strays close.
 */
function buildNetwork(): { nodes: Node[]; edges: Edge[] } {
  const random = seeded(20260820);
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  // One POP, upper left, clear of both the logo above and the heading below.
  nodes.push({ x: 0.09, y: 0.3, kind: 'pop' });

  // Splitters, spread across the top half.
  const splitterStart = nodes.length;
  const SPLITTERS = 5;
  for (let i = 0; i < SPLITTERS; i += 1) {
    nodes.push({
      x: 0.32 + random() * 0.14,
      y: 0.08 + (i / (SPLITTERS - 1)) * 0.5 + (random() - 0.5) * 0.05,
      kind: 'splitter',
    });
    edges.push({ from: 0, to: splitterStart + i });
  }

  // Premises hanging off each splitter — the fan-out.
  for (let i = 0; i < SPLITTERS; i += 1) {
    const parent = splitterStart + i;
    const count = 3 + Math.floor(random() * 3);
    for (let j = 0; j < count; j += 1) {
      nodes.push({
        x: 0.64 + random() * 0.28,
        y: nodes[parent]!.y + (j - (count - 1) / 2) * 0.075 + (random() - 0.5) * 0.04,
        kind: 'premises',
      });
      edges.push({ from: parent, to: nodes.length - 1 });
    }
  }

  return { nodes, edges };
}

const NETWORK = buildNetwork();

/** How many pieces of light are in flight at once. */
const PULSE_COUNT = 26;

function buildPulses(edgeCount: number): Pulse[] {
  const random = seeded(915_022);
  return Array.from({ length: PULSE_COUNT }, () => ({
    edge: Math.floor(random() * edgeCount),
    // Spread along the path so they do not all set off together.
    t: random(),
    // Varied, so the movement never falls into a visible rhythm.
    speed: 0.05 + random() * 0.11,
  }));
}

/**
 * The two grounds this can be drawn on.
 *
 * `onNavy` is the sign-in page and the rail: pale strokes on #0B2B5B.
 * `onCanvas` is the console's own background — the light grey between cards —
 * where the same picture has to be drawn in navy to be visible at all.
 *
 * The colours were emerald until now: `rgba(167, 243, 208)`, left over from the
 * theme this application had before Jua. The sweep covered screens and missed a
 * canvas, so the sign-in page has been quietly off-brand ever since.
 */
export type NetworkTone = 'onNavy' | 'onCanvas';

const PALETTE: Record<
  NetworkTone,
  { link: string; node: string; premises: string; halo: string; pulse: string }
> = {
  onNavy: {
    link: 'rgba(255, 255, 255, 0.10)',
    // Navy-100 rather than the old emerald. Same job, right family.
    node: 'rgba(199, 218, 242, 0.75)',
    premises: 'rgba(255, 255, 255, 0.28)',
    halo: 'rgba(199, 218, 242, ',
    pulse: 'rgba(255, 255, 255, ',
  },
  onCanvas: {
    // Navy on the light ground. Stronger alphas because a pale line on #F1EFE8
    // is nothing at all, and there is no text behind it to protect: every piece
    // of content in this application sits on an opaque card.
    link: 'rgba(11, 43, 91, 0.10)',
    node: 'rgba(11, 43, 91, 0.30)',
    premises: 'rgba(11, 43, 91, 0.22)',
    halo: 'rgba(11, 43, 91, ',
    pulse: 'rgba(255, 107, 0, ',
  },
};

export function LiveNetwork({
  className = '',
  tone = 'onNavy',
}: {
  className?: string;
  tone?: NetworkTone;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    const { nodes, edges } = NETWORK;
    const pulses = buildPulses(edges.length);
    /* Resolved once per mount rather than per frame — it never changes mid-run. */
    const paint = PALETTE[tone];

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    let width = 0;
    let height = 0;
    let frame = 0;
    let last = 0;

    /**
     * Size to the element, not the window.
     *
     * The panel is half the screen and changes with it, and drawing at CSS
     * pixels on a retina display gives visibly soft lines — hence the device
     * pixel ratio, capped at 2 because beyond that the cost is real and the
     * difference is not.
     */
    const resize = () => {
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = Math.max(1, Math.floor(width * ratio));
      canvas.height = Math.max(1, Math.floor(height * ratio));
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
    };

    const at = (node: Node) => ({ x: node.x * width, y: node.y * height });

    const draw = () => {
      context.clearRect(0, 0, width, height);

      // The links themselves: faint, so the text above stays the brightest
      // thing on the panel.
      context.lineWidth = 1;
      context.strokeStyle = paint.link;
      for (const edge of edges) {
        const from = at(nodes[edge.from]!);
        const to = at(nodes[edge.to]!);
        context.beginPath();
        context.moveTo(from.x, from.y);
        context.lineTo(to.x, to.y);
        context.stroke();
      }

      // Nodes. The POP is the anchor and gets a halo; premises are specks.
      for (const node of nodes) {
        const point = at(node);
        const radius = node.kind === 'pop' ? 5 : node.kind === 'splitter' ? 3 : 1.8;

        if (node.kind === 'pop') {
          const halo = context.createRadialGradient(point.x, point.y, 0, point.x, point.y, 26);
          halo.addColorStop(0, `${paint.halo}0.22)`);
          halo.addColorStop(1, `${paint.halo}0)`);
          context.fillStyle = halo;
          context.beginPath();
          context.arc(point.x, point.y, 26, 0, Math.PI * 2);
          context.fill();
        }

        context.fillStyle = node.kind === 'premises' ? paint.premises : paint.node;
        context.beginPath();
        context.arc(point.x, point.y, radius, 0, Math.PI * 2);
        context.fill();
      }

      // The light in flight.
      for (const pulse of pulses) {
        const edge = edges[pulse.edge]!;
        const from = at(nodes[edge.from]!);
        const to = at(nodes[edge.to]!);

        const x = from.x + (to.x - from.x) * pulse.t;
        const y = from.y + (to.y - from.y) * pulse.t;

        // Brightest as it sets off, dimmest as it arrives — attenuation, and
        // the reason this reads as light rather than as a moving dot.
        const alpha = 0.85 * (1 - pulse.t * 0.75);

        const glow = context.createRadialGradient(x, y, 0, x, y, 7);
        glow.addColorStop(0, `${paint.pulse}${alpha})`);
        glow.addColorStop(1, `${paint.pulse}0)`);
        context.fillStyle = glow;
        context.beginPath();
        context.arc(x, y, 7, 0, Math.PI * 2);
        context.fill();

        context.fillStyle = `${paint.pulse}${alpha})`;
        context.beginPath();
        context.arc(x, y, 1.6, 0, Math.PI * 2);
        context.fill();
      }

      /**
       * Erase where the words are.
       *
       * `destination-out` removes what has already been painted, so this is a
       * mask rather than a wash of background colour over the top — it works
       * whatever the gradient behind happens to be, and it cannot end up a
       * slightly-wrong shade of green.
       *
       * It is belt and braces on top of the layout. The graph is already kept
       * out of the bottom-left, but a pulse crossing a long edge can still
       * drift near the heading, and this guarantees the text always sits on
       * clean ground. Cheaper than being careful, and it cannot regress.
       */
      context.globalCompositeOperation = 'destination-out';

      // The heading and strapline, bottom-left.
      const headingMask = context.createRadialGradient(
        width * 0.04,
        height * 0.84,
        0,
        width * 0.04,
        height * 0.84,
        Math.max(width, height) * 0.62,
      );
      headingMask.addColorStop(0, 'rgba(0,0,0,0.95)');
      headingMask.addColorStop(0.55, 'rgba(0,0,0,0.35)');
      headingMask.addColorStop(1, 'rgba(0,0,0,0)');
      context.fillStyle = headingMask;
      context.fillRect(0, 0, width, height);

      // The strapline at the very bottom, full width.
      const footerMask = context.createLinearGradient(0, height * 0.88, 0, height);
      footerMask.addColorStop(0, 'rgba(0,0,0,0)');
      footerMask.addColorStop(1, 'rgba(0,0,0,0.85)');
      context.fillStyle = footerMask;
      context.fillRect(0, height * 0.88, width, height * 0.12);

      context.globalCompositeOperation = 'source-over';
    };

    /**
     * Advance by elapsed time, not by frame.
     *
     * A 120 Hz laptop would otherwise run this at twice the speed of a 60 Hz
     * one. `last` is reset whenever the loop restarts, so returning to a tab
     * after an hour does not teleport every pulse to the far end.
     */
    const step = (now: number) => {
      const elapsed = last === 0 ? 0 : Math.min((now - last) / 1000, 0.05);
      last = now;

      for (const pulse of pulses) {
        pulse.t += pulse.speed * elapsed;
        if (pulse.t >= 1) {
          pulse.t = 0;
          // A fresh edge each time round, so the traffic keeps rearranging
          // itself without anything needing to be random per frame.
          pulse.edge = Math.floor(Math.random() * edges.length);
        }
      }

      draw();
      frame = requestAnimationFrame(step);
    };

    const start = () => {
      if (frame !== 0 || reduceMotion.matches) return;
      last = 0;
      frame = requestAnimationFrame(step);
    };

    const stop = () => {
      if (frame === 0) return;
      cancelAnimationFrame(frame);
      frame = 0;
    };

    /** Nothing moving still deserves a composed picture, not an empty panel. */
    const settle = () => {
      stop();
      resize();
      draw();
    };

    const onVisibility = () => (document.hidden ? stop() : start());
    const onMotionChange = () => (reduceMotion.matches ? settle() : start());

    const observer = new ResizeObserver(() => {
      resize();
      // Redraw immediately so a drag-resize does not leave a stretched frame
      // on screen while paused.
      if (frame === 0) draw();
    });

    resize();
    draw();
    if (!reduceMotion.matches && !document.hidden) start();

    observer.observe(canvas);
    document.addEventListener('visibilitychange', onVisibility);
    reduceMotion.addEventListener('change', onMotionChange);

    return () => {
      stop();
      observer.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      reduceMotion.removeEventListener('change', onMotionChange);
    };
    // `tone` re-runs the effect, which is correct: the whole picture is drawn
    // in different colours and there is nothing incremental to update.
  }, [tone]);

  return (
    <canvas
      ref={canvasRef}
      // Decorative. A screen reader announcing "canvas" on a sign-in page is
      // noise, and there is no information here that the page does not already
      // say in words.
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 h-full w-full ${className}`}
    />
  );
}
